import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { createLink } from '@tanstack/react-router';

import ActionIcon from './ActionIcon';
import BdiCdiGraphic from './BdiCdiGraphic';

import usePlatformTraffickingLandingRoute from '@/features/navigation/hooks/usePlatformTraffickingLandingRoute';
import { Button } from '@/shared/components/button';

const LinkButton = createLink(Button);

const HowItWorks = () => {
  const traffickingLink = usePlatformTraffickingLandingRoute();

  return (
    <Box pt={1}>
      <Box
        p={3}
        m={2}
        borderRadius={1}
        sx={{ float: 'right', bgcolor: 'background.paper' }}
      >
        <ActionIcon sx={{ fontSize: 48 }} />

        <Typography pt={2.5}>Review your optimizations</Typography>
        <Typography pt={1} pb={3}>
          Make changes to campaigns and ad groups.
        </Typography>

        <LinkButton to={traffickingLink} variant='contained' fullWidth>
          Go to Trafficking
        </LinkButton>
      </Box>

      <Box>
        <Typography color='textTertiary' fontSize='1.2rem'>
          Background
        </Typography>
        <Typography
          pt={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          By default media generally delivers according to the distribution of
          the target population. Sales, however, are shaped and skewed based on
          consumer preferences and demand. In order to distribute media more
          effectively it is necessary to combine both population and sales data.
          <br />
          <br />
          This algorithm provides CPG advertisers with a greater ability to
          study, plan, manage and react to different markets and different
          consumption behaviors regardless of population at a global, national,
          or regional level so they can create customizable biddable media
          strategies at scale to increase sales through attributable campaigns
          and programs.
          <br />
          <br />
          Double down on more effective and higher ROI marketing through
          biddable media, with the ability to identify and invest in higher
          value audiences on demand.
        </Typography>

        <Typography color='textTertiary' pt={4} fontSize='1.2rem'>
          Definitions of BDI and CDI
        </Typography>
        <Typography
          py={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          BDI/CDI have long been used as proven effectiveness sales drivers
          within the CPG vertical to help marketers understand how a brand or
          category is performing in a particular market.
          <br />
          <br />
          Brand Development Index (BDI) is an index of how well a brand performs
          in a market relative to its average performance across all markets.
        </Typography>

        <Box
          borderRadius={1}
          p={2}
          sx={{ bgcolor: (theme) => theme.palette.primary[50] }}
        >
          <Typography color='primary.dark'>
            BDI = (% Market Brand Sales / % Market Population) geo x 100
          </Typography>
        </Box>

        <Typography
          py={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          Category Development Index (CDI) is an index of how well a category
          performs in a market relative to its average performance across all
          markets.
        </Typography>

        <Box
          borderRadius={1}
          p={2}
          sx={{ bgcolor: (theme) => theme.palette.primary[50] }}
        >
          <Typography color='primary.dark'>
            CDI = (% Market Category Sales / % Market Population) geo x 100
          </Typography>
        </Box>

        <Typography pt={4} fontSize='1.2rem'>
          BDI / CDI Framework
        </Typography>
        <Typography
          pt={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          NIQ Precision Area data will indicate which BDI/CDI quadrant each
          location falls into and therefore inform the necessary media
          optimization strategy accordingly.
        </Typography>

        <Box pt={2} maxWidth={800} mx='auto'>
          <BdiCdiGraphic />
        </Box>
      </Box>
    </Box>
  );
};

export default HowItWorks;
